import { useState } from 'react';
import { TextConversation } from './index';
import { Note } from './Note';
import { SearchHistory } from './SearchHistory';
import { BankTransaction } from './BankTransaction';
import { LocationData } from './LocationData';
import { Email } from './Email';
import { Voicemail } from './Voicemail';

// Combined function to extract all evidence types
const cleanPart = (cleanedPart) => {
  // Clean up excessive whitespace between paragraphs (keep at most 2 newlines)
  cleanedPart = cleanedPart.replace(/\n{3,}/g, '\n\n');

  // Remove leading and trailing newlines
  cleanedPart = cleanedPart.replace(/^\n+|\n+$/g, '');
  
  return cleanedPart
};

// Helper function to group consecutive text messages
const groupConsecutiveTextMessages = (evidence, startIndex) => {
  if (!evidence || startIndex >= evidence.length || evidence[startIndex].type !== 'text-message') {
    return { messages: [], lastIndex: startIndex - 1 };
  }
  
  const messages = [];
  let currentIndex = startIndex;
  
  // Collect consecutive text messages
  while (currentIndex < evidence.length && evidence[currentIndex].type === 'text-message') {
    messages.push(evidence[currentIndex]);
    currentIndex++;
  }
  
  return { messages, lastIndex: currentIndex - 1 };
};

// StoryRenderer component
export const StoryRenderer = ({ text, clue, evidence }) => {
  const [showClue, setShowClue] = useState(false);
  
  // Track processed indices to avoid duplicates
  const processedIndices = new Set();
  
  const renderEvidenceItem = (itemIndex) => {
    if (!evidence || itemIndex >= evidence.length) return null;
    
    // Skip if this index has already been processed (to avoid duplicates)
    if (processedIndices.has(itemIndex)) return null;
    
    const item = evidence[itemIndex];
    
    // Handle text messages differently - group them if consecutive
    if (item.type === 'text-message') {
      // Get all consecutive text messages
      const { messages, lastIndex } = groupConsecutiveTextMessages(evidence, itemIndex);
      
      // Mark all these indices as processed
      for (let i = itemIndex; i <= lastIndex; i++) {
        processedIndices.add(i);
      }
      
      // If we have messages, render them in a conversation container
      return <TextConversation messages={messages} />;
    }
    
    // Mark this index as processed
    processedIndices.add(itemIndex);
    
    // Handle other evidence types normally
    switch(item.type) {
      case 'note':
        return <Note note={item} />;
      case 'search-history':
        return <SearchHistory search={item} />;
      case 'bank-transaction':
        return <BankTransaction transaction={item} />;
      case 'location-data':
        return <LocationData location={item} />;
      case 'email':
        return <Email email={item} />;
      case 'voicemail':
        return <Voicemail voicemail={item} />;
      default:
        return null;
    }
  };
  
  // Render the story with evidence items
  const renderStoryWithEvidence = () => {
    // If there's no text or evidence, just return the text
    if (!text) return <p className="story-text"></p>;
    if (!evidence || evidence.length === 0 || !text.includes('[EVIDENCE_ITEM_')) {
      return <p className="story-text">{text}</p>;
    }
    
    // Split the text by the evidence item markers
    const parts = text.split(/\[EVIDENCE_ITEM_(\d+)\]/);
    
    return (
      <div className="story-with-evidence">
        {parts.map((part, index) => {
          // Even indices are text parts
          if (index % 2 === 0) {
            return <p key={index} className="story-text">{cleanPart(part)}</p>;
          }
          
          // Odd indices are evidence indices
          const itemIndex = parseInt(part, 10);
          return (
            <div key={index} className="evidence-container">
              {renderEvidenceItem(itemIndex)}
            </div>
          );
        })}
      </div>
    );
  };
  
  return (
    <div className="story-renderer">
      {renderStoryWithEvidence()}
      
      {clue && (
        <div className="clue-container">
          <button 
            className="clue-button" 
            onClick={() => setShowClue(!showClue)}
          >
            {showClue ? 'Hide Clue' : 'Show Clue'}
          </button>
          
          {showClue && (
            <div className="clue-reveal">
              <p>Clue: <span className="highlighted-clue">{clue.word}</span></p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// InvestigationRenderer component
export const InvestigationRenderer = ({ text, evidence }) => {
  // Track processed indices to avoid duplicates
  const processedIndices = new Set();
  
  const renderEvidenceItem = (itemIndex) => {
    if (!evidence || itemIndex >= evidence.length) return null;
    
    // Skip if this index has already been processed (to avoid duplicates)
    if (processedIndices.has(itemIndex)) return null;
    
    const item = evidence[itemIndex];
    
    // Handle text messages differently - group them if consecutive
    if (item.type === 'text-message') {
      // Get all consecutive text messages
      const { messages, lastIndex } = groupConsecutiveTextMessages(evidence, itemIndex);
      
      // Mark all these indices as processed
      for (let i = itemIndex; i <= lastIndex; i++) {
        processedIndices.add(i);
      }
      
      // If we have messages, render them in a conversation container
      return <TextConversation messages={messages} />;
    }
    
    // Mark this index as processed
    processedIndices.add(itemIndex);
    
    // Handle other evidence types normally
    switch(item.type) {
      case 'note':
        return <Note note={item} />;
      case 'search-history':
        return <SearchHistory search={item} />;
      case 'bank-transaction':
        return <BankTransaction transaction={item} />;
      case 'location-data':
        return <LocationData location={item} />;
      case 'email':
        return <Email email={item} />;
      case 'voicemail':
        return <Voicemail voicemail={item} />;
      default:
        return null;
    }
  };
  
  // Render the investigation with evidence items
  const renderInvestigationWithEvidence = () => {
    // If there's no text or evidence, just return the text
    if (!text) return <p className="investigation-text"></p>;
    if (!evidence || evidence.length === 0 || !text.includes('[EVIDENCE_ITEM_')) {
      return <p className="investigation-text">{text}</p>;
    }
    
    // Split the text by the evidence item markers
    const parts = text.split(/\[EVIDENCE_ITEM_(\d+)\]/);
    
    return (
      <div className="investigation-with-evidence">
        {parts.map((part, index) => {
          // Even indices are text parts
          if (index % 2 === 0) {
            return <p key={index} className="investigation-text">{cleanPart(part)}</p>;
          }
          
          // Odd indices are evidence indices
          const itemIndex = parseInt(part, 10);
          return (
            <div key={index} className="evidence-container">
              {renderEvidenceItem(itemIndex)}
            </div>
          );
        })}
      </div>
    );
  };
  
  return (
    <div className="investigation-renderer">
      {renderInvestigationWithEvidence()}
    </div>
  );
};

// SolutionRenderer component
export const SolutionRenderer = ({ text, evidence }) => {
  // Track processed indices to avoid duplicates
  const processedIndices = new Set();
  
  const renderEvidenceItem = (itemIndex) => {
    if (!evidence || itemIndex >= evidence.length) return null;
    
    // Skip if this index has already been processed (to avoid duplicates)
    if (processedIndices.has(itemIndex)) return null;
    
    const item = evidence[itemIndex];
    
    // Handle text messages differently - group them if consecutive
    if (item.type === 'text-message') {
      // Get all consecutive text messages
      const { messages, lastIndex } = groupConsecutiveTextMessages(evidence, itemIndex);
      
      // Mark all these indices as processed
      for (let i = itemIndex; i <= lastIndex; i++) {
        processedIndices.add(i);
      }
      
      // If we have messages, render them in a conversation container
      return <TextConversation messages={messages} />;
    }
    
    // Mark this index as processed
    processedIndices.add(itemIndex);
    
    // Handle other evidence types normally
    switch(item.type) {
      case 'note':
        return <Note note={item} />;
      case 'search-history':
        return <SearchHistory search={item} />;
      case 'bank-transaction':
        return <BankTransaction transaction={item} />;
      case 'location-data':
        return <LocationData location={item} />;
      case 'email':
        return <Email email={item} />;
      case 'voicemail':
        return <Voicemail voicemail={item} />;
      default:
        return null;
    }
  };
  
  // Render the solution with evidence items
  const renderSolutionWithEvidence = () => {
    // If there's no text or evidence, just return the text
    if (!text) return <p className="solution-text"></p>;
    if (!evidence || evidence.length === 0 || !text.includes('[EVIDENCE_ITEM_')) {
      return <p className="solution-text">{text}</p>;
    }
    
    // Split the text by the evidence item markers
    const parts = text.split(/\[EVIDENCE_ITEM_(\d+)\]/);
    
    return (
      <div className="solution-with-evidence">
        {parts.map((part, index) => {
          // Even indices are text parts
          if (index % 2 === 0) {
            return <p key={index} className="solution-text">{cleanPart(part)}</p>;
          }
          
          // Odd indices are evidence indices
          const itemIndex = parseInt(part, 10);
          return (
            <div key={index} className="evidence-container">
              {renderEvidenceItem(itemIndex)}
            </div>
          );
        })}
      </div>
    );
  };
  
  return (
    <div className="solution-renderer">
      {renderSolutionWithEvidence()}
    </div>
  );
}; 